import React from 'react';
import { motion } from 'framer-motion';
import { PresentationIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const SNAK_SCREEN = 'https://cdn.magicpatterns.com/patterns/generated-images/f56b4488-53cb-4caa-ae80-a720cbf1c3eb.jpg';
interface Channel {
  label: string;
  logo?: string;
  icon?: typeof PresentationIcon;
}
const LEFT_CHANNELS: readonly Channel[] = [{
  label: 'Email / CRM',
  logo: 'https://cdn.magicpatterns.com/uploads/eaVbSy8gkaLMxVXZn426xD/image.png'
}, {
  label: 'WhatsApp',
  logo: 'https://cdn.magicpatterns.com/uploads/ff2xrTo8njegc9HQfBiHHs/image.jpg'
}, {
  label: 'Website',
  logo: 'https://cdn.magicpatterns.com/uploads/p8hxGk8PzPNEJJ1JuXxQRJ/image.png'
}, {
  label: 'Social',
  logo: 'https://cdn.magicpatterns.com/uploads/i3HXhvnPCcq7MnMVtgzoGz/image.png'
}];
const RIGHT_CHANNELS: readonly Channel[] = [{
  label: 'QR code',
  logo: 'https://cdn.magicpatterns.com/uploads/5EBPJkAW8wtsVjG6Pdmtzv/image.png'
}, {
  label: 'Congress',
  logo: 'https://cdn.magicpatterns.com/uploads/gFpiinUzyP3uSTeNYjp6JX/image.png'
}, {
  label: 'Rep iPad / IVA',
  icon: PresentationIcon
}, {
  label: 'Paid media',
  logo: 'https://cdn.magicpatterns.com/uploads/x8cuoAZY8NxEd27vHPMXd5/image.png'
}];
function ChannelTile({
  channel,
  align,
  delay
}: {
  channel: Channel;
  align: 'left' | 'right';
  delay: number;
}) {
  return <motion.li initial={{
    opacity: 0,
    x: align === 'left' ? -20 : 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay,
    duration: 0.45
  }} className={`flex min-h-0 flex-1 items-center gap-[clamp(0.5rem,1vw,0.9rem)] rounded-2xl border border-[var(--fg)]/10 bg-white px-[clamp(0.7rem,1.2vw,1.1rem)] py-[clamp(0.4rem,0.9vw,0.8rem)] shadow-sm ${align === 'right' ? 'flex-row-reverse text-right' : ''}`} data-id="element-90" data-mp="h9adhbkeluh4">
      {channel.icon ? <span className="flex h-[clamp(2rem,2.6vw,2.6rem)] w-[clamp(2rem,2.6vw,2.6rem)] flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]/10" data-id="element-91" data-mp="3vd0qt5tzo9v7">
          <channel.icon className="h-[clamp(1rem,1.3vw,1.25rem)] w-[clamp(1rem,1.3vw,1.25rem)] text-[var(--brand)]" aria-hidden="true" data-id="element-92" data-mp="3qgaf0up7w0fl" />
        </span> : <span className="flex h-[clamp(2rem,2.6vw,2.6rem)] w-[clamp(2rem,2.6vw,2.6rem)] flex-shrink-0 items-center justify-center rounded-xl bg-[var(--surface)] p-1.5" data-id="element-93" data-mp="3bm5wl584pvl">
          <img src={channel.logo} alt="" className="h-full w-full object-contain" data-id="element-94" data-mp="d6w756nsst8b" />
        </span>}
      <span className="min-w-0 flex-1 truncate text-[clamp(0.8rem,1.15vw,1.05rem)] font-extrabold tracking-[-0.02em] text-[var(--fg)]" data-id="element-95" data-mp="mxh1uj0cpdly">
        {channel.label}
      </span>
      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" aria-hidden="true" data-id="element-96" data-mp="10xjkw8kead2s" />
    </motion.li>;
}
export function SlideChannelEntryPoints({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-98" data-mp="1ki0jbodplcmv">
          Meet HCPs wherever attention already exists.
        </SlideBanner>} data-id="element-97" data-mp="1fi4fbp50hl7b">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-99" data-mp="15kzrzlzfjjox">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-100" data-mp="wsfyt11wq7bh">
          Distribution
        </p>
        <h2 className="max-w-4xl text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-101" data-mp="11m0pe8167z0n">
          One experience.{' '}
          <span className="text-[var(--brand)]" data-id="element-102" data-mp="ruw7p0k42024">Many entry points.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(0.9rem,1.8vw,1.6rem)] grid min-h-0 flex-1 grid-cols-12 items-center gap-[clamp(0.6rem,1.4vw,1.5rem)]" data-id="element-103" data-mp="dver38gyxt3u">
        <ul className="flex h-full min-h-0 flex-col justify-center gap-[clamp(0.4rem,0.9vw,0.8rem)] col-span-4" data-id="element-104" data-mp="at0fl6b7l5ol">
          {LEFT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="left" delay={0.25 + index * 0.08} data-id="element-105" data-mp="5up6ixsqmz7f" data-mpi={index} />)}
        </ul>

        <motion.div initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15,
        duration: 0.55
      }} className="relative col-span-4 flex h-full min-h-0 items-center justify-center" data-id="element-106" data-mp="3s98cq77k782t">
          <motion.span animate={{
          scale: [1, 1.12, 1],
          opacity: [0.28, 0.06, 0.28]
        }} transition={{
          duration: 3.4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute h-[min(58%,18rem)] w-[min(58%,18rem)] rounded-full border-[3px] border-[var(--brand)]" aria-hidden="true" data-id="element-107" data-mp="3eas42aq4z7i1" />
          <img src={SNAK_SCREEN} alt="A single Snak experience shared across every channel" className="relative max-h-full w-auto rounded-[1.5rem] object-contain drop-shadow-[0_22px_50px_rgba(23,32,51,0.2)]" data-id="element-108" data-mp="17h6fjfx6r9wj" />
          <span className="absolute bottom-0 rounded-full bg-[var(--brand)] px-[clamp(0.7rem,1.2vw,1.1rem)] py-[clamp(0.25rem,0.5vw,0.4rem)] text-[clamp(0.6rem,0.85vw,0.78rem)] font-bold uppercase tracking-[0.18em] text-white shadow-md" data-id="element-109" data-mp="1otmv2w89asmq">
            One Snak
          </span>
        </motion.div>

        <ul className="flex h-full min-h-0 flex-col justify-center gap-[clamp(0.4rem,0.9vw,0.8rem)] col-span-4" data-id="element-110" data-mp="1142a67sjllat">
          {RIGHT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="right" delay={0.25 + index * 0.08} data-id="element-111" data-mp="1b0x74m963dmz" data-mpi={index} />)}
        </ul>
      </div>
    </InteriorSlide>;
}